<script>
import {
  defineAsyncComponent,
  useTemplateRef,
  onMounted,
  onUnmounted,
  nextTick,
  ref,
  computed,
  watch,
} from 'vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useIsMobile } from 'dashboard/composables/useMobileLayout';
import { useTrack } from 'dashboard/composables';
import { createKeyboardEventsFromLabel } from 'dashboard/components/widgets/modal/constants';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';

import CannedResponse from './CannedResponse.vue';
import CommandResponse, { COMMANDS } from './CommandResponse.vue';
import ReplyToMessage from './ReplyToMessage.vue';
import ResizableTextArea from 'shared/components/ResizableTextArea.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import { getReplyMaxLength } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { debounce } from '@chatwoot/utils';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import ConversationApi from 'dashboard/api/conversations';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  selectSignature,
  convertMarkdownToHtml,
  convertHtmlToPlain,
  resolveAlternateSenderEmail,
  findAdditionalSignatureHtml,
  findCountrySignatureHtml,
} from 'dashboard/helper/editorHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  CMD_AI_ASSIST,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';
import { OPEN_AI_OPTIONS } from 'shared/constants/openai';
const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

// Minimum confidence before we trust a detection: for the reply draft, and for the
// stored conversation language we compare it against.
//
// The bars are per provider because the numbers are not comparable. fastText returns
// softmax probabilities; lingua returns a confidence relative to the other candidate
// languages, which sits far lower for the same certainty, and cs/sk always split the
// mass between them. Detections carry the provider that produced them — see
// Integrations::LanguageDetection — and anything stored before that stamp existed
// came from fastText.
//
// A lingua number is no longer a raw model score either: the service resolves the
// answer and reports its own confidence in it. Czech typed without diacritics reaches
// ~0.95 when the text carries a giveaway word, but only ~0.5 when it does not — which
// lands exactly on the draft bar below, so a plainly worded draft is trusted or not on
// a knife edge. Move this in step with HIGH_CONFIDENCE_THRESHOLD on the Ruby side; the
// two are mirrored by hand.
const LANGUAGE_DETECTION_THRESHOLDS = {
  fasttext: { draft: 0.9, conversation: 0.8 },
  lingua: { draft: 0.5, conversation: 0.6 },
};
const LEGACY_LANGUAGE_DETECTION_PROVIDER = 'fasttext';

// Backstop against pathological short drafts. The confidence bar above does the real
// filtering — sampled drafts between 9 and 27 characters never produced a confident
// wrong detection — but a two-character token can still come back confidently wrong
// ("jj" reads as Ganda at 0.9), which would raise a mismatch warning out of nowhere.
// Kept well under the shortest draft that legitimately warns (16 chars), since the
// check re-runs as the agent types and a warning delayed is better than one invented.
const MIN_DRAFT_LANGUAGE_LENGTH = 16;

export default {
  components: {
    ArticleSearchPopover,
    AttachmentPreview,
    AudioRecorder,
    Banner,
    CannedResponse,
    CommandResponse,
    EmojiInput,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ResizableTextArea,
    WhatsappTemplates,
    WootMessageEditor,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  props: {
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['togglePopout'],
  setup(props) {
    const {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setSignatureFlagForInbox,
    } = useUISettings();

    const isMobile = useIsMobile();
    const replyEditor = useTemplateRef('replyEditor');
    const replyBottomPanelRef = ref(null);

    const emojiButtonElement = computed(() => {
      return replyBottomPanelRef.value?.emojiButtonRef?.$el;
    });

    let resizeObserver = null;
    const maxEditorHeight = ref('60vh');
    const maxEditorHeightFull = ref('60vh');

    const calculateMaxHeight = () => {
      const conversationBox = document.querySelector('.conversation-box');
      if (conversationBox && replyEditor.value) {
        const containerHeight = conversationBox.clientHeight;
        let subtractions = [];
        const subtractionsSelectors = [
          '.reply-email-head',
          '.reply-to-panel',
          '.reply-message-footer',
          '.ProseMirror-menubar',
          '.html_email_signature',
        ];
        subtractionsSelectors.forEach(selector => {
          const element = document.querySelector(selector);
          if (element) {
            subtractions.push(element.clientHeight);
          }
        });

        const totalSubtractions = subtractions.reduce(
          (acc, curr) => acc + curr,
          0
        );
        const calculatedHeight = Math.min(
          Math.floor((containerHeight - totalSubtractions) * 0.8),
          containerHeight - 350
        );
        const calculatedFullHeight = Math.min(
          Math.floor(
            (window.innerHeight - totalSubtractions) * 0.8,
            containerHeight - 150
          )
        );
        maxEditorHeight.value = `${calculatedHeight}px`;
        maxEditorHeightFull.value = `${calculatedFullHeight}px`;

        replyEditor.value.style.setProperty(
          '--dynamic-reply-editor-max-height',
          maxEditorHeight.value
        );
        replyEditor.value.style.setProperty(
          '--dynamic-reply-editor-max-height-full',
          maxEditorHeightFull.value
        );
      }
    };

    const customReplyBoxHeightPercent = computed(() => {
      const {
        reply_box_height_mode: heightMode,
        reply_box_height_percent: heightPercent,
      } = uiSettings.value || {};
      if (heightMode !== 'custom') return 0;
      return Math.min(100, Math.max(0, Math.round(Number(heightPercent)) || 0));
    });

    const hasCustomReplyBoxHeight = computed(
      () => !props.popoutReplyBox && customReplyBoxHeightPercent.value > 0
    );

    const applyCustomReplyBoxMinHeight = () => {
      if (!replyEditor.value) return;
      if (hasCustomReplyBoxHeight.value) {
        replyEditor.value.style.setProperty(
          '--custom-reply-editor-min-height',
          `${customReplyBoxHeightPercent.value}vh`
        );
      } else {
        replyEditor.value.style.removeProperty(
          '--custom-reply-editor-min-height'
        );
      }
    };

    watch(
      () => [hasCustomReplyBoxHeight.value, customReplyBoxHeightPercent.value],
      applyCustomReplyBoxMinHeight,
      { flush: 'post' }
    );

    let observerRetryFrame = null;

    const setupHeightObserver = (attempt = 0) => {
      const conversationBox = document.querySelector('.conversation-box');
      if (!conversationBox) {
        // Element may not be in the DOM yet; retry on next frame (capped).
        if (attempt < 30) {
          observerRetryFrame = requestAnimationFrame(() =>
            setupHeightObserver(attempt + 1)
          );
        }
        return;
      }

      calculateMaxHeight();

      resizeObserver = new ResizeObserver(() => {
        calculateMaxHeight();
      });
      resizeObserver.observe(conversationBox);
    };

    const hideEmojiPickerOnScroll = ref(null);

    const setupScrollListener = hidePickerFn => {
      hideEmojiPickerOnScroll.value = hidePickerFn;
      const conversationPanel = document.querySelector('.conversation-panel');
      if (conversationPanel) {
        conversationPanel.addEventListener('scroll', hidePickerFn);
      }
    };

    const cleanupScrollListener = () => {
      if (hideEmojiPickerOnScroll.value) {
        const conversationPanel = document.querySelector('.conversation-panel');
        if (conversationPanel) {
          conversationPanel.removeEventListener(
            'scroll',
            hideEmojiPickerOnScroll.value
          );
        }
      }
    };

    onMounted(() => {
      nextTick(() => {
        setupHeightObserver();
        applyCustomReplyBoxMinHeight();
      });
    });

    onUnmounted(() => {
      if (observerRetryFrame) {
        cancelAnimationFrame(observerRetryFrame);
        observerRetryFrame = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      cleanupScrollListener();
    });

    return {
      uiSettings,
      updateUISettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setSignatureFlagForInbox,
      isMobile,
      replyEditor,
      replyBottomPanelRef,
      emojiButtonElement,
      hasCustomReplyBoxHeight,
      maxEditorHeight,
      calculateMaxHeight,
      setupScrollListener,
      cleanupScrollListener,
    };
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      isRecordingAudio: false,
      recordingAudioState: '',
      recordingAudioDurationText: '',
      isUploading: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      mentionSearchKey: '',
      hasSlashCommand: false,
      hasCommandShortcut: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      emailSubject: '',
      includeOriginalMessage: null, // Will be set to account setting default in mounted()
      doAutoSaveDraft: () => {},
      draftLoadedForConversationId: null,
      showWhatsAppTemplatesModal: false,
      requestContactInfoTemplatesOnly: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      hasMentionResults: false,
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      allowSignature: false,
      draftLanguage: null,
      detectDraftLanguageDebounced: () => {},
      resolveAfterSend: false,
      isSendingMessage: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      currentAccount: 'accounts/getCurrentAccount',
      smsInboxes: 'inboxes/getSMSInboxes',
    }),
    allowToggleSignature() {
      return this.currentAccount?.allow_toggle_signature ?? true;
    },
    defaultIncludeOriginalMessage() {
      return this.currentAccount?.include_previous_email_message !== false;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.isPrivate &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO)
      );
    },
    showRichContentEditor() {
      if (this.isOnPrivateNote || this.isRichEditorEnabled) {
        return true;
      }

      if (this.isAPIInbox) {
        const {
          display_rich_content_editor: displayRichContentEditor = false,
        } = this.uiSettings;
        return displayRichContentEditor;
      }

      return false;
    },
    assignedAgent: {
      get() {
        return this.currentChat.meta.assignee;
      },
      set(agent) {
        const agentId = agent ? agent.id : 0;
        this.$store.dispatch('setCurrentChatAssignee', agent);
        this.$store
          .dispatch('assignAgent', {
            conversationId: this.currentChat.id,
            agentId,
          })
          .then(() => {
            useAlert(this.$t('CONVERSATION.CHANGE_AGENT'));
          });
      },
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (this.assignedAgent.id !== this.currentUser.id) {
          return true;
        }
      }

      return false;
    },
    hasWhatsappTemplates() {
      return !!this.$store.getters['inboxes/getWhatsAppTemplates'](this.inboxId)
        .length;
    },
    // Outside the 24 hour window WhatsApp accepts templates only, so a regular
    // reply would be created here and then fail on the provider.
    isOutsideWhatsappWindow() {
      return this.isAWhatsAppChannel && this.currentChat.can_reply === false;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      if (this.isPrivate) {
        return this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT');
      }
      if (this.isOutsideWhatsappWindow) {
        return this.$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATE_ONLY_INPUT');
      }
      return this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    // A pending conversation belongs to the agent bot until an agent opens it.
    isPendingWithBot() {
      return (
        !this.isPrivate &&
        this.currentChat?.status === 'pending' &&
        !!this.isBotActive()
      );
    },
    // Nothing fetches the customer's answer, see Inbox#replies_paused?
    isInboxRepliesPaused() {
      return !this.isPrivate && !!this.inbox?.replies_paused;
    },
    isReplyButtonDisabled() {
      if (this.isPendingWithBot) return true;
      if (this.isInboxRepliesPaused) return true;
      if (this.isATwitterInbox) return true;
      if (!this.isPrivate && !this.canSendSmsFromPhoneInbox) return true;
      // A free form reply outside the window is rejected by WhatsApp, the agent
      // has to use a template instead.
      if (!this.isPrivate && this.isOutsideWhatsappWindow) return true;
      if (
        !this.isPrivate &&
        this.isAWebWidgetInbox &&
        this.currentContact?.availability_status !== 'online'
      ) {
        const hasValidEmail =
          this.currentContact?.email && this.currentContact.email.trim() !== '';
        const canUseContinuityEmail =
          this.inbox?.continuity_via_email && hasValidEmail;
        const flags = this.inbox?.selected_feature_flags || [];
        const allowOfflineReply = flags.includes('allow_offline_reply');
        if (!canUseContinuityEmail && !allowOfflineReply) return true;
      }
      if (this.hasAttachments) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    isSendDisabled() {
      // Also disabled while a send is in flight, so a second click cannot
      // trigger a duplicate message before the editor is cleared.
      return this.isReplyButtonDisabled || this.isSendingMessage;
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      const maxLength = getReplyMaxLength({
        isPrivate: this.isPrivate,
        channelType: this.channelType,
        isSms: this.isASmsInbox,
        isFacebookComment: this.conversationType === 'facebook_comment',
        isEmailHtml: this.htmlMode,
      });
      // The signature goes out in the same message, and Allegro rejects a body over its cap.
      if (
        this.isMarketplaceSignatureChannel &&
        this.allowSignature &&
        this.signatureToApply
      ) {
        return maxLength - this.signatureToApply.length;
      }
      return maxLength;
    },
    showFileUpload() {
      if (this.conversationType === 'facebook_comment') {
        return false;
      }
      // SMS Brána and DIDWW send only text; the backend refuses a reply with attachments there
      if (this.isASmsBranaChannel || this.isADidwwChannel) {
        return false;
      }
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAInstagramChannel ||
        this.isAKauflandChannel ||
        this.isAnAllegroChannel
      );
    },
    shouldSendViaEmail() {
      if (
        !this.isPrivate &&
        this.isAWebWidgetInbox &&
        this.currentContact?.availability_status !== 'online'
      ) {
        const hasValidEmail =
          this.currentContact?.email && this.currentContact.email.trim() !== '';
        const canUseContinuityEmail =
          this.inbox?.continuity_via_email && hasValidEmail;
        return canUseContinuityEmail;
      }
      return false;
    },
    sendActionText() {
      if (this.isPrivate) {
        return this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      if (this.isAnEmailChannel || this.shouldSendViaEmail) {
        return this.$t('CONVERSATION.REPLYBOX.SEND_EMAIL');
      }
      if (this.isASmsInbox || this.isPhoneInbox) {
        return this.$t('CONVERSATION.REPLYBOX.SEND_SMS');
      }
      return this.$t('CONVERSATION.REPLYBOX.SEND_CHAT');
    },
    // A reply in a phone conversation leaves as an SMS through one of the
    // account's SMS inboxes, see ContinueOnSmsService. If the account has no
    // SMS inbox, the reply is never delivered.
    canSendSmsFromPhoneInbox() {
      return !this.isPhoneInbox || this.smsInboxes.length > 0;
    },
    sendDisabledTooltip() {
      if (this.isPendingWithBot) {
        return this.$t('CONVERSATION.PENDING_WITH_BOT_BANNER');
      }
      if (this.isInboxRepliesPaused) {
        return this.$t('CONVERSATION.INBOX_DELETE_REQUESTED_BANNER');
      }
      if (!this.isPrivate && !this.canSendSmsFromPhoneInbox) {
        return this.$t('CONVERSATION.REPLYBOX.NO_SMS_INBOX');
      }
      return '';
    },
    replyButtonLabel() {
      // On phones the long label pushes the button out of the reply box
      if (this.isMobile) {
        return this.isPrivate
          ? this.$t('CONVERSATION.REPLYBOX.CREATE')
          : this.$t('CONVERSATION.REPLYBOX.SEND');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '(⌘ + ↵)'
        : '(↵)';
      return `${this.sendActionText} ${keyLabel}`;
    },
    sendAndResolveButtonLabel() {
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? '(⌘ + ⇧ + ↵)'
        : '(⌥ + ↵)';
      const resolveText = this.$t('CONVERSATION.HEADER.RESOLVE_ACTION');
      return `${this.sendActionText} & ${resolveText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'is-popout': this.popoutReplyBox,
        'custom-height': this.hasCustomReplyBoxHeight && this.isAnEmailChannel,
      };
    },
    draftFiles() {
      const draft = this.$store.getters['messageDraft/getDraft'](
        this.conversationIdByRoute
      );
      if (!draft) return [];
      return this.isOnPrivateNote
        ? draft.privateFiles || []
        : draft.publicFiles || [];
    },
    isFetchingDraft() {
      return this.$store.getters['messageDraft/getUIFlags'].isFetching;
    },
    uploadingFiles() {
      return this.$store.getters['messageDraft/getUploadingFiles'](
        this.conversationIdByRoute
      );
    },
    hasAttachments() {
      return this.draftFiles.length || this.uploadingFiles.length;
    },
    isRichEditorEnabled() {
      return this.isAWebWidgetInbox || this.isAnEmailChannel;
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    draftAudioUrl() {
      const audioFile = this.draftFiles.find(f =>
        f.content_type?.startsWith('audio/')
      );
      return audioFile?.url || '';
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isTwilioSMS() {
      return this.isATwilioSMSChannel;
    },
    // A marketplace inbox serves buyers from several countries and signs a reply
    // in the conversation's country, which the server works out and sends as
    // `country` (Conversations::CountryResolver).
    isMarketplaceSignatureChannel() {
      return this.isAKauflandChannel || this.isAnAllegroChannel;
    },
    // Private notes never carry a signature.
    signatureEnabled() {
      return (
        (this.isAnEmailChannel ||
          this.isTwilioSMS ||
          this.isMarketplaceSignatureChannel) &&
        !this.isOnPrivateNote
      );
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      if (this.showRichContentEditor && this.targetSchema === 'html') {
        const doc = new DOMParser().parseFromString(this.message, 'text/html');
        const hasText =
          (doc.body.textContent || '').trim().replace(/\n/g, '').length > 0;

        if (hasText) {
          return false;
        }

        const hasImages = doc.body.querySelector('img') !== null;
        return !hasImages;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    currentMailSubject() {
      return this.currentChat?.additional_attributes?.mail_subject || '';
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureAvailable() {
      return this.signatureEnabled && !!this.signatureToApply;
    },
    htmlMode() {
      return this.isAnEmailChannel && !this.isOnPrivateNote;
    },
    messagePlainText() {
      if (this.htmlMode) {
        return convertHtmlToPlain(this.message);
      }
      return this.message;
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    audioRecordFormat() {
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    effectiveInboxSignature() {
      if (this.isMarketplaceSignatureChannel) {
        return (
          findCountrySignatureHtml(
            this.inbox?.country_signatures,
            this.currentChat?.country
          ) || this.inbox?.html_signature
        );
      }
      // Additive: use the signature mapped to the alias the reply will be sent
      // from, otherwise fall back to the inbox's primary html_signature.
      const alternateEmail = resolveAlternateSenderEmail(
        this.currentChat?.messages,
        this.inbox?.email_aliases?.map(emailAlias => emailAlias.email),
        this.inbox?.email
      );
      const additionalSignature = findAdditionalSignatureHtml(
        this.inbox?.additional_html_signatures,
        alternateEmail
      );
      return additionalSignature || this.inbox?.html_signature;
    },
    signatureToApply() {
      const isPlain = this.isTwilioSMS || this.isMarketplaceSignatureChannel;
      return selectSignature(
        this.signatureEnabled,
        this.effectiveInboxSignature,
        // An agent's own signature is written for email and may carry a website or
        // contacts, which marketplaces forbid in a message to the buyer.
        this.isMarketplaceSignatureChannel ? null : this.messageSignature,
        this.currentUser.available_name,
        this.currentUser.email,
        this.currentUser.position,
        this.currentUser.custom_line,
        this.currentUser.email_signature_picture_url,
        isPlain ? 'plain' : 'html',
        // A marketplace reply reads like a letter: a blank line before the sign-off.
        this.isMarketplaceSignatureChannel ? '\n\n' : '\n'
      );
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    connectedPortalBaseUrl() {
      return this.inbox.help_center?.base_url || '';
    },
    targetSchema() {
      return this.isAnEmailChannel && !this.isOnPrivateNote
        ? 'html'
        : 'markdown';
    },
    editorContainsSignature() {
      return this.message && this.message.includes('id="html_email_signature"');
    },
    // The language the inbox is configured to speak, used only when detection found
    // nothing to go on. Opt-in per inbox, since the locale it reuses is not reliably
    // configured everywhere. Nothing is stored on the conversation for this — it stands in
    // here and for the translation target, and nowhere else.
    inboxFallbackLanguage() {
      return this.inbox?.fallback_conversation_language || null;
    },
    // A detection we trust wins; otherwise the language the inbox states it speaks.
    conversationLanguage() {
      const thresholds =
        LANGUAGE_DETECTION_THRESHOLDS[this.conversationLanguageProvider];
      if (
        thresholds &&
        this.conversationLanguageConfidence >= thresholds.conversation
      ) {
        return this.currentChat?.additional_attributes?.conversation_language;
      }

      return this.inboxFallbackLanguage;
    },
    conversationLanguageConfidence() {
      return this.currentChat?.additional_attributes
        ?.conversation_language_confidence;
    },
    conversationLanguageProvider() {
      return (
        this.currentChat?.additional_attributes
          ?.conversation_language_provider || LEGACY_LANGUAGE_DETECTION_PROVIDER
      );
    },
    hasConfidentConversationLanguage() {
      // Weak detections are stored and refined as more messages arrive, so we only
      // trust the stored language for the mismatch warning once it clears its
      // provider's bar. Legacy detections (no confidence stored) are ignored.
      const thresholds =
        LANGUAGE_DETECTION_THRESHOLDS[this.conversationLanguageProvider];
      if (!thresholds) return false;
      if (this.conversationLanguageConfidence >= thresholds.conversation)
        return true;

      return !!this.inboxFallbackLanguage;
    },
    showLanguageWarning() {
      return (
        !this.isOnPrivateNote &&
        !!this.draftLanguage &&
        !!this.conversationLanguage &&
        this.hasConfidentConversationLanguage &&
        this.draftLanguage !== this.conversationLanguage
      );
    },
    languageWarningMessage() {
      return this.$t('CONVERSATION.REPLYBOX.LANGUAGE_MISMATCH', {
        draftLanguage: this.languageDisplayName(this.draftLanguage),
        conversationLanguage: this.languageDisplayName(
          this.conversationLanguage
        ),
      });
    },
  },
  watch: {
    showEmojiPicker(isVisible) {
      if (isVisible) {
        this.setupScrollListener(this.hideEmojiPicker);
      } else {
        this.cleanupScrollListener();
      }
    },
    currentChat(conversation) {
      const { can_reply: canReply } = conversation;

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // However, this update does not trigger the `currentChat` watcher.
    // We can add a deep watcher to it, but then, that would be too broad of a net to cast
    // And would impact performance too. So we watch the messages directly.
    // The watcher here is `deep` too, because the messages array is mutated and
    // not replaced. So, a shallow watcher would not catch the change.
    'currentChat.messages': {
      handler() {
        if (this.isFacebookComment) {
          this.fetchAndSetReplyTo();
        }
      },
      deep: true,
    },
    async conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.draftLoadedForConversationId = null;
        this.resetRecorderState();
        this.restoreSignatureFromSettings();
        this.draftLanguage = null;
        this.message = '';
        this.toEmails = '';
        this.ccEmails = '';
        this.bccEmails = '';
        this.emailSubject = '';
        await this.$store.dispatch('messageDraft/fetch', conversationId);
        this.loadDraftContentFromStore();
      }
    },
    message(updatedMessage) {
      const startsWithSlash = updatedMessage.startsWith('/');
      const startsWithExclamation = updatedMessage.startsWith('!');

      // Determine if the user is potentially typing a slash command.
      // This is true if the message starts with a slash and the rich content editor is not active.
      this.hasSlashCommand = startsWithSlash && !this.showRichContentEditor;

      // Determine if the user is potentially typing a !-command.
      // Allow !-commands in both simple and rich content editors, but not if already in private mode with exact command match
      this.hasCommandShortcut =
        startsWithExclamation &&
        this.isBotActive() &&
        !this.isExactCommandMatch(updatedMessage);

      this.showMentions = this.hasSlashCommand || this.hasCommandShortcut;

      // If a slash command is active, extract the command text after the slash.
      // If not, reset the mentionSearchKey.
      this.mentionSearchKey =
        this.hasSlashCommand || this.hasCommandShortcut
          ? updatedMessage.substring(1)
          : '';

      this.doAutoSaveDraft();
      this.detectDraftLanguageDebounced();
    },
    toEmails() {
      this.doAutoSaveDraft();
    },
    ccEmails() {
      this.doAutoSaveDraft();
    },
    bccEmails() {
      this.doAutoSaveDraft();
    },
    emailSubject() {
      this.doAutoSaveDraft();
    },
    includeOriginalMessage() {
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.saveDraftToBackend(this.conversationIdByRoute, oldReplyType, {
        immediate: true,
      });
      this.loadDraftContentFromStore();
    },
    draftFiles: {
      handler() {
        this.$nextTick(() => {
          this.calculateMaxHeight();
        });
      },
      deep: true,
    },
  },

  async mounted() {
    if (this.conversationIdByRoute) {
      await this.$store.dispatch(
        'messageDraft/fetch',
        this.conversationIdByRoute
      );
      this.loadDraftContentFromStore();
    }
    // Don't use the keyboard listener mixin here as the events here are supposed to be
    // working even if input/textarea is focussed.
    document.addEventListener('paste', this.onPaste);

    // Set default value for includeOriginalMessage based on account setting
    if (this.includeOriginalMessage === null) {
      this.includeOriginalMessage = this.defaultIncludeOriginalMessage;
    }

    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraftToBackend(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.detectDraftLanguageDebounced = debounce(() => {
      this.detectDraftLanguage();
    }, 800);

    this.restoreSignatureFromSettings();

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(BUS_EVENTS.FOCUS_MESSAGE_EDITOR, this.focusMessageEditor);
  },
  unmounted() {
    document.removeEventListener('paste', this.onPaste);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(BUS_EVENTS.FOCUS_MESSAGE_EDITOR, this.focusMessageEditor);
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
  },
  methods: {
    toggleSignature(global = true) {
      this.allowSignature = !this.allowSignature;
      if (global) {
        this.setSignatureFlagForInbox(this.channelType, this.allowSignature);
      }
      this.$nextTick(() => {
        this.calculateMaxHeight();
      });
    },
    onSignatureInserted() {
      this.toggleSignature(false);
    },
    restoreSignatureFromSettings() {
      if (this.editorContainsSignature) {
        this.allowSignature = false;
      } else {
        this.allowSignature = this.fetchSignatureFlagFromUISettings(
          this.channelType
        );
      }
    },
    handleInsert(article) {
      const { url, title } = article;
      if (this.isRichEditorEnabled) {
        // Removing empty lines from the title
        const lines = title.split('\n');
        const nonEmptyLines = lines.filter(line => line.trim() !== '');
        const filteredMarkdown = nonEmptyLines.join(' ');
        emitter.emit(
          BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
          `[${filteredMarkdown}](${url})`
        );
      } else {
        this.addIntoEditor(
          `${this.$t('CONVERSATION.REPLYBOX.INSERT_READ_MORE')} ${url}`
        );
      }

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });
    },
    resetRecorderState() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
    },
    translateDraft() {
      emitter.emit(CMD_AI_ASSIST, OPEN_AI_OPTIONS.TRANSLATE);
    },
    languageDisplayName(code) {
      if (!code) return '';
      try {
        const locale = this.$i18n?.locale || 'en';
        const names = new Intl.DisplayNames([locale], { type: 'language' });
        return names.of(code) || code;
      } catch {
        return code;
      }
    },
    async detectDraftLanguage() {
      // Only warn on outgoing replies, not private notes.
      if (this.isOnPrivateNote) {
        this.draftLanguage = null;
        return;
      }

      const text = convertHtmlToPlain(this.message || '').trim();
      if (text.length < MIN_DRAFT_LANGUAGE_LENGTH) {
        this.draftLanguage = null;
        return;
      }

      // Skip unless we have a confidently-detected conversation language to
      // compare against — otherwise there's nothing trustworthy to warn about.
      if (
        !this.conversationLanguage ||
        !this.hasConfidentConversationLanguage
      ) {
        return;
      }

      try {
        const { data } = await ConversationApi.detectLanguage(
          this.conversationIdByRoute,
          text
        );
        // Only trust a detection we're confident about, to avoid false warnings.
        // The response carries the provider that produced it, since the bar differs.
        const thresholds =
          LANGUAGE_DETECTION_THRESHOLDS[
            data?.provider || LEGACY_LANGUAGE_DETECTION_PROVIDER
          ];
        this.draftLanguage =
          thresholds && data?.confidence >= thresholds.draft
            ? data.language
            : null;
      } catch (error) {
        this.draftLanguage = null;
      }
    },
    saveDraftToBackend(conversationId, replyType, { immediate = false } = {}) {
      if (conversationId !== this.draftLoadedForConversationId) return;
      const isPrivateNote = replyType === REPLY_EDITOR_MODES.NOTE;
      const payload = { conversationId, immediate };
      if (isPrivateNote) {
        payload.privateContent = this.message;
      } else {
        payload.publicContent = this.message;
        payload.ccEmails = this.ccEmails
          ? this.ccEmails
              .split(',')
              .map(e => e.trim())
              .filter(Boolean)
          : [];
        payload.bccEmails = this.bccEmails
          ? this.bccEmails
              .split(',')
              .map(e => e.trim())
              .filter(Boolean)
          : [];
        payload.toEmails = this.toEmails
          ? this.toEmails
              .split(',')
              .map(e => e.trim())
              .filter(Boolean)
          : [];
        payload.includeOriginalMessage = this.includeOriginalMessage;
        payload.emailSubject = this.emailSubject;
      }
      this.$store.dispatch('messageDraft/update', payload);
    },
    loadDraftContentFromStore() {
      const conversationId = this.conversationIdByRoute;
      this.draftLoadedForConversationId = conversationId;
      const draft =
        this.$store.getters['messageDraft/getDraft'](conversationId);
      if (!draft) return;

      const isPrivateNote = this.replyType === REPLY_EDITOR_MODES.NOTE;
      this.message =
        (isPrivateNote ? draft.privateContent : draft.publicContent) || '';

      if (!isPrivateNote) {
        this.emailSubject = draft.emailSubject || '';
        this.toEmails = (draft.toEmails || []).join(', ');
        this.ccEmails = (draft.ccEmails || []).join(', ');
        this.bccEmails = (draft.bccEmails || []).join(', ');
        if (draft.includeOriginalMessage !== undefined) {
          this.includeOriginalMessage = draft.includeOriginalMessage;
        }

        this.restoreAudioRecorderStateFromDraft(draft);
      }
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
            this.hideMentions();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            if (this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
        'Alt+Enter': {
          action: e => {
            if (this.isAValidEvent('enter') && !this.isOnPrivateNote) {
              this.onSendAndResolve();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Shift+Enter': {
          action: e => {
            if (this.isAValidEvent('cmd_enter') && !this.isOnPrivateNote) {
              this.onSendAndResolve();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        ...createKeyboardEventsFromLabel('AI_ASSIST_MENU', {
          action: e => {
            e.preventDefault();
            const ninja = document.querySelector('ninja-keys');
            ninja.open({ parent: 'ai_assist' });
          },
          allowOnFocusedInput: true,
        }),
        ...createKeyboardEventsFromLabel('AI_ASSIST_GRAMMAR', {
          action: e => {
            e.preventDefault();
            if (this.messagePlainText !== '') {
              emitter.emit(CMD_AI_ASSIST, OPEN_AI_OPTIONS.FIX_SPELLING_GRAMMAR);
            }
          },
          allowOnFocusedInput: true,
        }),
        ...createKeyboardEventsFromLabel('AI_ASSIST_UNIFY_LANG', {
          action: e => {
            e.preventDefault();
            if (this.message && this.message.trim() !== '') {
              emitter.emit(
                CMD_AI_ASSIST,
                OPEN_AI_OPTIONS.TRANSLATE_TO_MAJORITY_LANGUAGE
              );
            }
          },
          allowOnFocusedInput: true,
        }),
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !(this.showMentions && this.hasMentionResults) &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      const data = e.clipboardData.files;
      if (!this.showRichContentEditor && data.length !== 0) {
        this.$refs.messageInput.$el.blur();
      }
      if (!data.length || !data[0]) {
        return;
      }

      // Block file paste for facebook comment replies (allow on private notes)
      if (
        this.conversationType === 'facebook_comment' &&
        !this.isOnPrivateNote
      ) {
        return;
      }

      // For rich editor, only handle non-image files as regular attachments
      // Images are handled directly by the Editor's paste handler
      if (this.showRichContentEditor) {
        data.forEach(file => {
          // Only non-image files go to regular attachments for rich editor
          if (!file.type.startsWith('image/')) {
            const { name, type, size } = file;
            this.onFileUpload({ name, type, size, file: file });
          }
        });
      } else {
        // For plain text editor, all files go to regular attachments
        data.forEach(file => {
          const { name, type, size } = file;
          this.onFileUpload({ name, type, size, file: file });
        });
      }
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    addEmailRecipient(email) {
      if (!email || !email.trim()) return;

      const existingEmails = this.toEmails
        .split(',')
        .map(e => e.trim().toLowerCase())
        .filter(e => e.length > 0);

      const emailLower = email.trim().toLowerCase();
      if (existingEmails.includes(emailLower)) {
        return;
      }

      if (this.toEmails && this.toEmails.trim().length > 0) {
        this.toEmails = `${this.toEmails}, ${email.trim()}`;
      } else {
        this.toEmails = email.trim();
      }
    },
    openWhatsappTemplateModal() {
      this.requestContactInfoTemplatesOnly = false;
      this.showWhatsAppTemplatesModal = true;
    },
    openContactInfoTemplateModal() {
      this.requestContactInfoTemplatesOnly = true;
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
      this.requestContactInfoTemplatesOnly = false;
    },
    onClickSelfAssign() {
      const {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        avatar_url,
      } = this.currentUser;
      const selfAssign = {
        account_id,
        availability_status,
        available_name,
        email,
        id,
        name,
        role,
        thumbnail: avatar_url,
      };
      this.assignedAgent = selfAssign;
    },
    async confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      if (!this.showMentions || !this.hasMentionResults) {
        const isOnWhatsApp = this.isAWhatsAppChannel;
        // When users send messages containing both text and attachments on Instagram, Instagram treats them as separate messages.
        // Although Chatwoot combines these into a single message, Instagram sends separate echo events for each component.
        // This can create duplicate messages in Chatwoot. To prevent this issue, we'll handle text and attachments as separate messages.

        let value = this.message;

        if (this.allowSignature && this.signatureToApply) {
          value += this.signatureToApply;
        }

        const isOnInstagram = this.isAInstagramChannel;
        let sendSuccess = false;
        try {
          if ((isOnWhatsApp || isOnInstagram) && !this.isPrivate) {
            await this.sendMessageAsMultipleMessages(value);
          } else {
            const messagePayload = this.getMessagePayload(value);
            await this.sendMessage(messagePayload);
          }
          sendSuccess = true;
        } catch (error) {
          // Message will remain in editor if sending fails
          sendSuccess = false;
        }

        // Only clear the message if it was sent successfully
        if (sendSuccess) {
          this.clearMessage();
          this.hideEmojiPicker();
          if (this.resolveAfterSend) {
            this.resolveAfterSend = false;
            emitter.emit(CMD_RESOLVE_CONVERSATION);
          }
        }
      }
    },
    async sendMessageAsMultipleMessages(message) {
      const messages = this.getMultipleMessagesPayload(message);
      await Promise.all(
        messages.map(messagePayload => this.sendMessage(messagePayload))
      );
    },
    sendMessageAnalyticsData(isPrivate) {
      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.allowSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      // Guards against repeated clicks or keyboard shortcut presses while the
      // previous send is still in flight, which would send the same message
      // multiple times (the editor is only cleared once the request resolves).
      if (this.isSendingMessage) {
        return;
      }
      this.isSendingMessage = true;
      try {
        const undefinedVariables = getUndefinedVariablesInMessage({
          message: this.message,
          variables: this.messageVariables,
        });
        if (undefinedVariables.length > 0) {
          const undefinedVariablesCount =
            undefinedVariables.length > 1 ? undefinedVariables.length : 1;
          this.undefinedVariableMessage = this.$t(
            'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
            {
              undefinedVariablesCount,
              undefinedVariables: undefinedVariables.join(', '),
            }
          );

          const ok = await this.$refs.confirmDialog.showConfirmation();
          if (ok) {
            await this.confirmOnSendReply();
          }
        } else {
          await this.confirmOnSendReply();
        }
      } finally {
        this.isSendingMessage = false;
      }
    },
    async onSendAndResolve() {
      if (
        this.isOnPrivateNote ||
        this.isReplyButtonDisabled ||
        this.isSendingMessage
      ) {
        return;
      }
      this.resolveAfterSend = true;
      try {
        await this.onSendReply();
      } finally {
        this.resolveAfterSend = false;
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        const conversationId = this.currentChat.id;
        if (messagePayload.private) {
          this.$store.commit('messageDraft/CLEAR_MESSAGE_DRAFT_PRIVATE', {
            conversationId,
          });
        } else {
          await this.$store.dispatch(
            'messageDraft/clearPublicAndReload',
            conversationId
          );
          this.loadDraftContentFromStore();
        }
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        // validation failures (RecordInvalid) come back as `message`, not `error`
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage);
        throw error;
      }
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    replaceText(message) {
      let updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });

      if (this.targetSchema === 'html' && !updatedMessage.startsWith('<')) {
        updatedMessage = convertMarkdownToHtml(updatedMessage);
      }

      setTimeout(() => {
        useTrack(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    applyCannedResponseMeta(meta = {}) {
      if (!meta) return;

      if (
        typeof meta.subject === 'string' &&
        meta.subject.trim() &&
        this.isAnEmailChannel &&
        !this.emailSubject.trim()
      ) {
        this.emailSubject = meta.subject;
      }

      if (Array.isArray(meta.files) && meta.files.length) {
        this.appendCannedAttachments(meta.files);
      }
    },
    appendCannedAttachments(cannedFiles) {
      const conversationId = this.conversationIdByRoute;
      cannedFiles.forEach(file => {
        this.$store.dispatch('messageDraft/addAttachment', {
          conversationId,
          file: file.blob_signed_id,
          fileMeta: {
            name: file.filename,
            size: file.byte_size,
            type: file.file_type,
          },
          isPrivate: this.isOnPrivateNote,
        });
      });
    },
    focusMessageEditor() {
      this.$nextTick(() => {
        if (this.showRichContentEditor) {
          this.$refs.editor?.focusEditor();
        } else {
          this.$refs.messageInput?.focus();
        }
      });
    },
    replaceCommand(command) {
      this.message = ``;
      this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
      this.$nextTick(() => {
        this.message = `${command} `;
      });
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel) this.replyType = mode;
      if (this.showRichContentEditor) {
        if (this.isRecordingAudio) {
          this.toggleAudioRecorder();
        }
        return;
      }
      this.$nextTick(() => this.$refs.messageInput.focus());
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    insertIntoTextEditor(text, selectionStart, selectionEnd) {
      const { message } = this;
      const newMessage =
        message.slice(0, selectionStart) +
        text +
        message.slice(selectionEnd, message.length);
      this.message = newMessage;
    },
    addIntoEditor(content) {
      if (this.showRichContentEditor) {
        this.updateEditorSelectionWith = content;
        this.onFocus();
      }
      if (!this.showRichContentEditor) {
        const { selectionStart, selectionEnd } = this.$refs.messageInput.$el;
        this.insertIntoTextEditor(content, selectionStart, selectionEnd);
      }
    },
    clearMessage() {
      this.message = '';
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
      this.restoreSignatureFromSettings();
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.isRecorderAudioStopped = !this.isRecordingAudio;
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.isRecordingAudio) {
        return;
      }
      if (!this.isRecorderAudioStopped) {
        this.isRecorderAudioStopped = true;
        this.$refs.audioRecorderInput.stopRecording();
      } else if (this.isRecorderAudioStopped) {
        this.$refs.audioRecorderInput.playPause();
      }
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    hideMentions() {
      this.showMentions = false;
    },
    isBotActive() {
      return this.inbox.agent_bot?.id;
    },
    isExactCommandMatch(messageText) {
      // Don't show palette if in private mode and the text exactly matches an existing command
      if (!this.isOnPrivateNote) {
        return false;
      }

      return COMMANDS.some(command =>
        messageText.trim().startsWith(`!${command}`)
      );
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraftToBackend(this.conversationIdByRoute, this.replyType, {
        immediate: true,
      });
    },
    onFocus() {
      this.isFocused = true;
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      return file && this.onFileUpload({ ...file, isRecordedAudio: true });
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    onFileUpload(file) {
      if (!file) return;
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file);
      } else {
        this.onIndirectFileUpload(file);
      }
    },
    attachFile({ blob, file }) {
      const fileToUpload = blob ? blob.signed_id : file.file;
      this.$store.dispatch('messageDraft/addAttachment', {
        conversationId: this.conversationIdByRoute,
        file: fileToUpload,
        fileMeta: { name: file.name, size: file.size, type: file.type },
        isPrivate: file?.isRecordedAudio ? false : this.isOnPrivateNote,
      });
    },
    removeDraftAttachment(attachmentId) {
      this.$store.dispatch('messageDraft/removeAttachment', {
        conversationId: this.conversationIdByRoute,
        attachmentId,
      });
    },
    cancelUpload(tempId) {
      this.$store.dispatch('messageDraft/cancelUpload', {
        conversationId: this.conversationIdByRoute,
        tempId,
      });
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.message) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: false,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      let messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }

      if (this.includeOriginalMessage && !this.isOnPrivateNote) {
        messagePayload.contentAttributes = {
          ...messagePayload.contentAttributes,
          include_original_message: this.includeOriginalMessage,
        };
      }

      if (this.isAnEmailChannel && !this.isOnPrivateNote) {
        messagePayload.contentAttributes ||= {};
        messagePayload.contentAttributes.email ||= {};
        messagePayload.contentAttributes.email.html_content ||= {};
        messagePayload.contentAttributes.email.html_content.full =
          messagePayload.message;
        messagePayload.contentAttributes.email_subject = this.emailSubject;
      }

      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },

    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      this.inReplyTo = this.currentChat?.messages?.find(message => {
        if (message.id === replyToMessageId) {
          return true;
        }
        return false;
      });

      // Auto-set reply-to for Facebook comments to last incoming message
      if (!this.inReplyTo && this.isFacebookComment) {
        const messages = this.currentChat?.messages || [];
        const lastMessage = [...messages]
          .reverse()
          .find(m => m.message_type === 0 && !m.content_attributes?.deleted);
        if (lastMessage) {
          this.inReplyTo = lastMessage;
          LocalStorage.updateJsonStore(
            replyStorageKey,
            this.conversationId,
            lastMessage.id
          );
        }
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.removeAudioDraftFiles();
    },
    restoreAudioRecorderStateFromDraft(draft) {
      const publicFiles = draft.publicFiles || [];
      const hasAudioFile = publicFiles.some(f =>
        f.content_type?.startsWith('audio/')
      );
      if (hasAudioFile) {
        this.isRecordingAudio = true;
        this.isRecorderAudioStopped = true;
        this.recordingAudioState = 'stopped';
      }
    },
    removeAudioDraftFiles() {
      this.draftFiles
        .filter(f => f.content_type?.startsWith('audio/'))
        .forEach(f => {
          this.$store.dispatch('messageDraft/removeAttachment', {
            conversationId: this.conversationIdByRoute,
            attachmentId: f.id,
          });
        });
    },
  },
};
</script>

<template>
  <Banner
    v-if="showSelfAssignBanner"
    action-button-variant="ghost"
    color-scheme="secondary"
    class="mb-2 rounded-lg banner--self-assign"
    :banner-message="$t('CONVERSATION.NOT_ASSIGNED_TO_YOU')"
    has-action-button
    :action-button-label="$t('CONVERSATION.ASSIGN_TO_ME')"
    @primary-action="onClickSelfAssign"
  />
  <div ref="replyEditor" class="reply-box" :class="replyBoxClass">
    <ReplyTopPanel
      :mode="replyType"
      :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
      :characters-remaining="charactersRemaining"
      :is-popout="popoutReplyBox"
      @set-reply-mode="setReplyMode"
      @toggle-popout="$emit('togglePopout')"
    />
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      :portal-base-url="connectedPortalBaseUrl"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <div class="reply-box__top">
      <ReplyToMessage
        v-if="shouldShowReplyToMessage"
        :message="inReplyTo"
        @dismiss="resetReplyToMessage"
      />
      <CannedResponse
        v-if="showMentions && hasSlashCommand"
        v-on-clickaway="hideMentions"
        class="normal-editor__canned-box"
        :search-key="mentionSearchKey"
        :inbox-id="inboxId"
        @replace="
          (content, meta) => {
            replaceText(content);
            applyCannedResponseMeta(meta);
          }
        "
        @has-results="hasMentionResults = $event"
      />
      <CommandResponse
        v-if="showMentions && hasCommandShortcut"
        v-on-clickaway="hideMentions"
        class="normal-editor__command-box"
        :search-key="mentionSearchKey"
        @replace="replaceCommand"
        @has-results="hasMentionResults = $event"
      />
      <EmojiInput
        v-if="showEmojiPicker"
        teleport
        :trigger-element="emojiButtonElement"
        :on-click="addIntoEditor"
      />
      <ReplyEmailHead
        v-if="showReplyHead"
        v-model:cc-emails="ccEmails"
        v-model:bcc-emails="bccEmails"
        v-model:to-emails="toEmails"
        v-model:email-subject="emailSubject"
        v-model:include-original-message="includeOriginalMessage"
        :is-loading="isFetchingDraft"
      />
      <AudioRecorder
        v-if="showAudioRecorderEditor"
        ref="audioRecorderInput"
        :audio-record-format="audioRecordFormat"
        :initial-audio-url="draftAudioUrl"
        @recorder-progress-changed="onRecordProgressChanged"
        @finish-record="onFinishRecorder"
        @play="recordingAudioState = 'playing'"
        @pause="recordingAudioState = 'paused'"
      />
      <template v-else-if="!showRichContentEditor">
        <ResizableTextArea
          ref="messageInput"
          v-model="message"
          class="rounded-none input"
          :placeholder="messagePlaceHolder"
          :min-height="4"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
        />
        <div v-if="allowSignature" class="signature">
          {{ signatureToApply }}
        </div>
      </template>
      <WootMessageEditor
        v-else
        ref="editor"
        v-model="message"
        :editor-id="editorStateId"
        :conversation-id="conversationId"
        class="input flex-1"
        :is-private="isOnPrivateNote"
        :placeholder="messagePlaceHolder"
        :update-selection-with="updateEditorSelectionWith"
        :min-height="4"
        enable-variables
        :variables="messageVariables"
        :signature="signatureToApply"
        :allow-signature="allowSignature"
        :show-image-resize-toolbar="isAnEmailChannel"
        :channel-type="channelType"
        :target-schema="targetSchema"
        :inbox-id="inboxId"
        @typing-off="onTypingOff"
        @typing-on="onTypingOn"
        @focus="onFocus"
        @blur="onBlur"
        @toggle-user-mention="toggleUserMention"
        @toggle-canned-menu="toggleCannedMenu"
        @toggle-variables-menu="toggleVariablesMenu"
        @clear-selection="clearEditorSelection"
        @signature-inserted="onSignatureInserted"
        @add-email-recipient="addEmailRecipient"
        @canned-response-selected="applyCannedResponseMeta"
      />
    </div>

    <div class="reply-message-footer">
      <div
        v-if="hasAttachments && !showAudioRecorderEditor"
        class="attachment-preview-box"
        @paste="onPaste"
      >
        <AttachmentPreview
          class="mt-4 overflow-auto"
          :draft-files="draftFiles"
          :uploading-files="uploadingFiles"
          @remove-draft-attachment="removeDraftAttachment"
          @cancel-upload="cancelUpload"
        />
      </div>
      <Banner
        v-if="showLanguageWarning"
        class="mx-3 mt-2 rounded-lg"
        color-scheme="warning"
        :banner-message="languageWarningMessage"
        has-action-button
        action-button-icon="i-ph-translate"
        :action-button-label="$t('CONVERSATION.REPLYBOX.TRANSLATE_DRAFT')"
        @primary-action="translateDraft"
      />
      <ReplyBottomPanel
        ref="replyBottomPanelRef"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :has-whatsapp-templates="hasWhatsappTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isSendDisabled"
        :send-disabled-tooltip="sendDisabledTooltip"
        :mode="replyType"
        :on-file-upload="onFileUpload"
        :on-send="onSendReply"
        :on-send-and-resolve="onSendAndResolve"
        :send-and-resolve-button-text="sendAndResolveButtonLabel"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-audio-recorder="showAudioRecorder"
        :show-editor-toggle="isAPIInbox && !isOnPrivateNote"
        :show-emoji-picker="showEmojiPicker"
        :show-file-upload="showFileUpload"
        :show-signature-button="
          allowToggleSignature ? isSignatureAvailable : false
        "
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        :allow-signature="allowSignature"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @request-contact-info-template="openContactInfoTemplateModal"
        @toggle-editor="toggleRichContentEditor"
        @replace-text="replaceText"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-signature="toggleSignature"
      />
    </div>
    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      :request-contact-info-only="requestContactInfoTemplatesOnly"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.banner--self-assign {
  @apply py-2;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  transition: height 2s cubic-bezier(0.37, 0, 0.63, 1);

  @apply relative border border-n-weak rounded-xl bg-n-solid-1;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;
  }

  &.custom-height {
    @apply flex flex-col;
    min-height: var(--custom-reply-editor-min-height);

    .reply-box__top {
      @apply flex-1 min-h-0 flex flex-col;

      textarea {
        @apply max-h-none;
        flex: 1 1 0;
      }
    }
  }
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative py-0 px-4 -mt-px;

  textarea {
    @apply shadow-none outline-none border-transparent bg-transparent m-0 max-h-60 min-h-[3rem] pt-4 pb-0 px-0 resize-none;
  }
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}

.normal-editor__command-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}

.signature {
  white-space: pre-wrap;
}
</style>
